import type { Selection } from 'd3-selection';
import { select } from 'd3-selection';
import $ from 'jquery';
import { identity, intersection, zip } from 'lodash-es';
import type { Observable } from 'rxjs';
import { combineLatest, EMPTY, map, switchMap, throttleTime } from 'rxjs';
import type { VperBucket } from '@/graphql/types';
import { formatDateTz, formatTimeTz } from '../utils/formatting';
import { timestampRangeOverlap } from '../utils/range';
import { applyNamingConvention, namingConventions$ } from '../utils/naming-conventions';
import { fixFloat, getDataType } from './data-types';
import type { DataSet } from './dataset';

interface LegendData {
    getVisibleTypes(): string[];
}

interface VperPeriod extends VperBucket {
    timestamp: number;
    timespan: {
        from: number;
        to: number;
    };
    vmax: {
        x?: number;
        y?: number;
        z?: number;
    };
    vper: {
        x?: number;
        y?: number;
        z?: number;
    };
    limits: {
        A1: number;
        A2: number;
        A3: number;
        complies: boolean;
    };
}

export const periodTranslations: Record<string, string> = {
    DAY: gettext('Day'),
    EVENING: gettext('Evening'),
    NIGHT: gettext('Night'),
};

export class VperTable {
    $container: JQuery<HTMLElement>;

    dataSet: DataSet;

    legendData: LegendData;

    pageBreak: JQuery<HTMLElement>;

    header: Selection<HTMLHeadingElement, unknown, HTMLElement, unknown>;

    infoMessage: Selection<HTMLParagraphElement, unknown, HTMLElement, unknown>;

    table: Selection<HTMLTableElement, unknown, HTMLElement, unknown>;

    tableHeader: Selection<HTMLTableRowElement, unknown, HTMLElement, unknown>;

    tableBody: Selection<HTMLTableSectionElement, unknown, HTMLElement, unknown>;

    shouldBeVisible$: Observable<boolean>;

    constructor(
        containerSelector: string,
        dataSet: DataSet,
        legendData: LegendData,
        responsive = true
    ) {
        this.$container = $(containerSelector);
        this.dataSet = dataSet;
        this.legendData = legendData;
        this.pageBreak = $("<div class='pagebreak'></div>").insertBefore(this.$container);

        const headerContainer = select(containerSelector).append('div');
        headerContainer.attr('class', 'header');
        this.header = headerContainer.append<HTMLHeadingElement>('h3')!;

        this.infoMessage = select(containerSelector).append<HTMLParagraphElement>('p')!;

        this.table = select(containerSelector).append<HTMLTableElement>('table')!;
        this.table.attr('class', 'table');
        this.table.classed('table-responsive-label', responsive);

        this.tableHeader = this.table.append('thead').append<HTMLTableRowElement>('tr');

        this.tableBody = this.table.append<HTMLTableSectionElement>('tbody');

        this.dataSet.on('loading-updated', () => {
            this.renderInfoMessage();
        });

        this.initTableNameUpdater();

        this.shouldBeVisible$ = this.createShouldBeVisible$();

        // Toggle the container's visibility upon updated to shouldBeVisible$.
        this.shouldBeVisible$.subscribe((visible) => {
            this.toggleContainerVisibility(visible);
        });

        this.shouldBeVisible$
            .pipe(
                switchMap((visible) =>
                    visible
                        ? // If we are allowed to be visible, we can start listening for
                          // updates to the visible date range and vper periods in the dataset.
                          // `throttleTime` on the date range update to improve performance while
                          // scrolling/zooming.
                          combineLatest([
                              this.dataSet.dateRange.observable$.pipe(
                                  throttleTime(300, undefined, { leading: true, trailing: true })
                              ),
                              this.dataSet.vperPeriods$,
                          ])
                        : // Stop further action if we are not allowed to be visible.
                          EMPTY
                )
            )
            .subscribe(() => {
                this.render();
            });
    }

    createShouldBeVisible$(): Observable<boolean> {
        return this.dataSet.combinedSettings$.pipe(map((x) => x.showVssTable));
    }

    toggleContainerVisibility(toggle: boolean): void {
        this.$container.toggle(toggle);
        this.pageBreak.toggle(toggle);
    }

    renderInfoMessage(): void {
        this.infoMessage.text(this.dataSet.loading ? gettext('LOADING') : gettext('No data found'));
    }

    showInfoMessage(): void {
        this.infoMessage.style('display', 'block');
        this.table.style('display', 'none');
    }

    showTable(): void {
        this.infoMessage.style('display', 'none');
        this.table.style('display', 'table');
    }

    initTableNameUpdater(): void {
        // Update the table name when the naming conventions change.
        namingConventions$.subscribe(() => {
            this.header.text(applyNamingConvention(getDataType('vper').graphName!));
        });
    }

    render(): void {
        const { vperPeriods, timezone } = this.dataSet;

        const { visibleMin, visibleMax } = this.dataSet;

        // Filter periods that are visible in the visible time window.
        const periods = (vperPeriods as VperPeriod[]).filter(({ timespan: { from, to } }) =>
            timestampRangeOverlap([from, to], [visibleMin, visibleMax])
        );

        if (periods.length === 0) {
            this.showInfoMessage();
            return;
        }

        this.showTable();

        const visibleTypes = this.legendData.getVisibleTypes();
        const visibleAxes = intersection(visibleTypes, ['x', 'y', 'z']);

        const vmaxTitle = applyNamingConvention(getDataType('vmax').title);
        const vperTitle = applyNamingConvention(getDataType('vper').title);
        const vperValuenames = getDataType('vper').valueNames;

        const headers = [
            gettext('Date'),
            gettext('Period'),
            ...visibleAxes.map((axis) => `${vmaxTitle} ${axis}`),
            ...visibleAxes.map((axis) => `${vperTitle} ${axis}`),
            ...vperValuenames!.map((valueName) => applyNamingConvention(valueName)),
            gettext('Remarks'),
        ];

        this.tableHeader
            .selectAll<HTMLTableCellElement, string>('th')
            .data(headers, identity)
            .join((enter) => enter.append('th').text((d) => d));

        const rows = this.tableBody
            .selectAll<HTMLTableRowElement, VperPeriod>('tr')
            .data(periods, (d) => `${d.timespan.from}-${d.timespan.to}`)
            .join('tr');

        rows.selectAll<HTMLTableCellElement, VperPeriod>('td')
            .data((d) => {
                // Sample to columns.
                const formattedDate = formatDateTz(timezone, d.timestamp);

                function formatTime(timestamp: number): string {
                    return formatTimeTz(timezone, timestamp);
                }

                const from = formatTime(d.timespan.from);
                const to = formatTime(d.timespan.to);
                let periodContent = d.period
                    ? `${periodTranslations[d.period]} (${from}-${to})`
                    : `${from}-${to}`;

                const completenessTitle = gettext('Completeness of measurement period');

                periodContent +=
                    ` <span title="${completenessTitle}">` +
                    `(${d.periodCompleteness?.completeness ?? 0}%)` +
                    `</span>`;

                function printValuePerAxis(
                    type: string,
                    getValueForAxis: (axis: string) => number | undefined
                ): (string | number)[] {
                    return visibleAxes.map((axis) => {
                        const value = getValueForAxis(axis);
                        return fixFloat(type, value!);
                    });
                }

                const veffContent = printValuePerAxis(
                    'vmax',
                    (axis) => d.vmax[axis as 'x' | 'y' | 'z']
                );

                const vperContent = printValuePerAxis(
                    'vper',
                    (axis) => d.vper[axis as 'x' | 'y' | 'z']
                );

                const rowData: (string | number)[] = [
                    formattedDate, // date
                    periodContent, // period
                    ...veffContent, // veff,max
                    ...vperContent, // vper
                    fixFloat('vmax', d.limits.A1),
                    fixFloat('vmax', d.limits.A2),
                    fixFloat('vmax', d.limits.A3),
                    d.limits.complies ? gettext('Complies') : gettext('Noncomplying'),
                ];

                // Merge headers and data together:
                return zip(headers, rowData).map((row) => ({
                    label: row[0] ?? '',
                    data: row[1] ?? '',
                }));
            })
            .join('td')
            .attr('data-label', (d) => d.label)
            .html((d) => String(d.data));
    }
}
